import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { researchAPI } from '@/lib/api';
import { toast } from 'sonner';
import { FlaskConical } from 'lucide-react';

// Optional: a parent lets numbers about how their child learns (never identity, writing or audio) join a research set.
const ResearchConsentBox = ({ studentId }) => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['research-consent', studentId],
    queryFn: async () => (await researchAPI.getConsent(studentId)).data,
    enabled: !!studentId,
  });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!data) return null;

  const change = async (granted) => {
    setBusy(true);
    try {
      await researchAPI.setConsent(studentId, granted, agree);
      await qc.invalidateQueries({ queryKey: ['research-consent', studentId] });
      setAgree(false);
      toast.success(granted ? 'Thank you for helping improve Semantic Vision' : 'Your child has been withdrawn and their rows deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
    setBusy(false);
  };

  return (
    <div className="border-4 border-black bg-white p-5 brutal-shadow-sm" data-testid="research-consent">
      <div className="flex items-center gap-2 mb-2"><FlaskConical size={20} /><h3 className="text-xl font-black uppercase">Help improve Semantic Vision</h3></div>
      <p className="text-sm text-gray-700 mb-3">{data.text}</p>
      {data.granted ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold" data-testid="research-status">Included since {new Date(data.granted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          <button onClick={() => change(false)} disabled={busy} className="px-4 py-2 border-2 border-black bg-white font-bold text-sm" data-testid="research-withdraw">Withdraw my child</button>
        </div>
      ) : (
        <div>
          <label className="flex items-start gap-2 text-sm mb-3">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="research-agree" />
            <span>I am this child's parent or guardian and I agree.</span>
          </label>
          <button onClick={() => change(true)} disabled={busy || !agree} className="px-4 py-2 border-2 border-black bg-amber-200 font-bold text-sm disabled:opacity-40" data-testid="research-join">Include my child</button>
        </div>
      )}
    </div>
  );
};

export default ResearchConsentBox;
